import axios from 'axios';
import CryptoJS from 'crypto-js';
import linkify from 'linkify-it';
import _escape from 'lodash/escape';
import { v4 as uuidv4 } from 'uuid';
import i18n from './i18n';
import createLogger from './logger';
import MESSAGE_TYPE from './messageType';
import store from './store';

const { getState } = store;
const logger = createLogger('util');

const linkifyInstance = linkify();
const markdownLinkPattern = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const markdownCodePattern = /`([^`]+)`/g;
const markdownStrongPattern = /\*\*(.+?)\*\*/g;
const markdownLinkDetectPattern = /\[[^\]]+\]\([^)\s]+\)/;
const markdownCodeDetectPattern = /`[^`]+`/;
const markdownStrongDetectPattern = /\*\*.+?\*\*/;
const markdownTableSeparatorPattern = /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/;
const markdownTokenPrefix = '__md_token__';

const isSafeLinkHref = (href: string) => /^(https?:\/\/|mailto:|tel:)/i.test(href);

const hasMarkdownSyntax = (content: string) => {
	if (!content || typeof content !== 'string') {
		return false;
	}

	return (
		/(^|\n)\s*(#{1,6}\s+\S+|>\s+\S+|[-*+]\s+\S+|\d+\.\s+\S+|(?:---+|\*\*\*+|___+)\s*$)/m.test(content) ||
		/(^|\n)\s*\|.+\|\s*$/m.test(content) ||
		markdownStrongDetectPattern.test(content) ||
		markdownCodeDetectPattern.test(content) ||
		markdownLinkDetectPattern.test(content)
	);
};

export function linkifyUrl(rawText: any) {
	const linkifyResult = linkifyInstance.match(rawText);

	const linkMap: any[] = [];

	const patternResult =
		linkifyResult &&
		linkifyResult.reduce((pre, current, idx) => {
			linkMap.push(`<a href='${linkifyResult[idx].url}' target='_blank'  class='textMessageLink'>${linkifyResult[idx].text}</a>`);
			return pre.replace(linkifyResult[idx].text, `%{${idx}}%`);
		}, rawText);
	const htmlText =
		linkMap.length > 0
			? linkMap.reduce((pre, current, idx) => {
					return pre.replace(`%{${idx}}%`, linkMap[idx]);
			  }, patternResult)
			: rawText;

	return { html: htmlText };
}

const preserveTokens = (text: string, pattern: RegExp, render: (...args: string[]) => string) => {
	const tokens: string[] = [];
	const output = text.replace(pattern, (...args) => {
		const token = `${markdownTokenPrefix}${tokens.length}__`;
		tokens.push(render(...(args.slice(1, -2) as string[])));
		return token;
	});

	return {
		output,
		restore: (value: string) =>
			tokens.reduce((result, tokenHtml, index) => result.replace(`${markdownTokenPrefix}${index}__`, tokenHtml), value),
	};
};

const renderInlineMarkdown = (content: string) => {
	const linkPreserved = preserveTokens(content, markdownLinkPattern, (label: string, href: string) => {
		if (!isSafeLinkHref(href)) {
			return _escape(`[${label}](${href})`);
		}

		return `<a href='${_escape(href)}' target='_blank' rel='noreferrer' class='textMessageLink'>${renderInlineMarkdown(
			label
		)}</a>`;
	});

	const codePreserved = preserveTokens(
		linkPreserved.output,
		markdownCodePattern,
		(code: string) => `<code>${_escape(code)}</code>`
	);

	let html = _escape(codePreserved.output);
	html = html.replace(markdownStrongPattern, '<strong>$1</strong>');
	html = linkifyUrl(html).html;
	html = codePreserved.restore(html);
	html = linkPreserved.restore(html);

	return html;
};

const splitTableCells = (line: string) =>
	line
		.trim()
		.replace(/^\|/, '')
		.replace(/\|$/, '')
		.split('|')
		.map(cell => cell.trim());

const renderMarkdownTable = (headerLine: string, bodyLines: string[]) => {
	const headers = splitTableCells(headerLine);
	const rows = bodyLines.map(splitTableCells);

	return `
		<div class='markdownTableWrap'>
			<table class='markdownTable'>
				<thead>
					<tr>${headers.map(header => `<th>${renderInlineMarkdown(header)}</th>`).join('')}</tr>
				</thead>
				<tbody>
					${rows.map(row => `<tr>${row.map(cell => `<td>${renderInlineMarkdown(cell)}</td>`).join('')}</tr>`).join('')}
				</tbody>
			</table>
		</div>
	`;
};

const isMarkdownHorizontalRule = (line: string) => /^(---+|\*\*\*+|___+)$/.test(line);

const isMarkdownHeading = (line: string) => /^(#{1,6})\s+/.test(line);

const isMarkdownQuote = (line: string) => /^>\s?/.test(line);

const isMarkdownUnorderedList = (line: string) => /^[-*+]\s+/.test(line);

const isMarkdownOrderedList = (line: string) => /^\d+\.\s+/.test(line);

const isMarkdownTableStart = (lines: string[], index: number) => {
	const currentLine = lines[index]?.trim();
	const nextLine = lines[index + 1]?.trim();

	return !!currentLine && currentLine.includes('|') && !!nextLine && markdownTableSeparatorPattern.test(nextLine);
};

const isParagraphBoundary = (lines: string[], index: number) => {
	const trimmedLine = lines[index]?.trim();

	if (!trimmedLine) {
		return true;
	}

	return (
		isMarkdownHorizontalRule(trimmedLine) ||
		isMarkdownHeading(trimmedLine) ||
		isMarkdownQuote(trimmedLine) ||
		isMarkdownUnorderedList(trimmedLine) ||
		isMarkdownOrderedList(trimmedLine) ||
		isMarkdownTableStart(lines, index)
	);
};

const renderMarkdown = (content: string) => {
	const lines = content.replace(/\r\n?/g, '\n').split('\n');
	const blocks: string[] = [];
	let index = 0;

	while (index < lines.length) {
		const currentLine = lines[index];
		const trimmedLine = currentLine.trim();

		if (!trimmedLine) {
			index += 1;
		} else if (isMarkdownHorizontalRule(trimmedLine)) {
			blocks.push('<hr />');
			index += 1;
		} else if (isMarkdownHeading(trimmedLine)) {
			const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
			if (!headingMatch) {
				index += 1;
			} else {
				const level = headingMatch[1].length;
				blocks.push(`<h${level}>${renderInlineMarkdown(headingMatch[2])}</h${level}>`);
				index += 1;
			}
		} else if (isMarkdownTableStart(lines, index)) {
			const headerLine = lines[index];
			const bodyLines: string[] = [];
			index += 2;
			while (index < lines.length && lines[index].trim() && lines[index].includes('|')) {
				bodyLines.push(lines[index]);
				index += 1;
			}
			blocks.push(renderMarkdownTable(headerLine, bodyLines));
		} else if (isMarkdownQuote(trimmedLine)) {
			const quoteLines: string[] = [];
			while (index < lines.length && isMarkdownQuote(lines[index].trim())) {
				quoteLines.push(lines[index].trim().replace(/^>\s?/, ''));
				index += 1;
			}
			blocks.push(`<blockquote>${quoteLines.map(renderInlineMarkdown).join('<br/>')}</blockquote>`);
		} else if (isMarkdownUnorderedList(trimmedLine)) {
			const items: string[] = [];
			while (index < lines.length && isMarkdownUnorderedList(lines[index].trim())) {
				items.push(lines[index].replace(/^\s*[-*+]\s+/, ''));
				index += 1;
			}
			blocks.push(`<ul>${items.map(item => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ul>`);
		} else if (isMarkdownOrderedList(trimmedLine)) {
			const items: string[] = [];
			while (index < lines.length && isMarkdownOrderedList(lines[index].trim())) {
				items.push(lines[index].replace(/^\s*\d+\.\s+/, ''));
				index += 1;
			}
			blocks.push(`<ol>${items.map(item => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ol>`);
		} else {
			const paragraphLines: string[] = [];
			while (index < lines.length && !isParagraphBoundary(lines, index)) {
				paragraphLines.push(lines[index]);
				index += 1;
			}

			blocks.push(`<p>${paragraphLines.map(renderInlineMarkdown).join('<br/>')}</p>`);
		}
	}

	return blocks.join('');
};

export const setParentCookie = (name: string, value: string, exdays: any = 365) => {
	window.parent.postMessage({ target: 'eightChat', command: 'notifySetCookie', options: { name, value, exdays } }, '*');
};

export const getParentCookie = (name: string) => {
	return window.parent.postMessage({ target: 'eightChat', command: 'notifyGetCookie', options: { name } }, '*');
};

export const deleteParentCookie = (name: string) => {
	window.parent.postMessage({ target: 'eightChat', command: 'notifyDeleteCookie', options: { name } }, '*');
};

const EXPANDED_STORAGE_PREFIX = 'super8_livechat_expanded_';

export const getExpandedPreference = (orgId: string): boolean => {
	try {
		return window.localStorage.getItem(`${EXPANDED_STORAGE_PREFIX}${orgId}`) === '1';
	} catch (e) {
		return false;
	}
};

export const setExpandedPreference = (orgId: string, expanded: boolean) => {
	try {
		window.localStorage.setItem(`${EXPANDED_STORAGE_PREFIX}${orgId}`, expanded ? '1' : '0');
	} catch (e) {
		// 第三方 iframe 的 storage 可能被瀏覽器隱私設定封鎖，靜默降級
	}
};

export async function downloadImage(file: any) {
	const fileName = file.replace(/^.*[\\/]/, '');
	const response = await axios.get(`${file}?time=${new Date().getTime()}`, {
		responseType: 'blob',
	});
	const imageURL = URL.createObjectURL(response.data);
	const link = document.createElement('a');
	link.href = imageURL;
	link.download = fileName;
	link.click();
}

export const showText = (content: any, isNoBreakline: any = false) => {
	let text = '';
	const clean = _escape(content);
	const res = linkifyUrl(clean);
	if (res.html) {
		text = res.html;
	}
	if (text && text.length > 0) {
		if (isNoBreakline) {
			text = text.replace(/(\r\n|\n|\r)/gm, ' ');
		} else {
			text = text.replace(/(\r\n|\n|\r)/gm, '<br/>');
		}
	}

	return text;
};

export const showMarkdownText = (content: any, isNoBreakline: any = false) => {
	if (typeof content !== 'string' || isNoBreakline || !hasMarkdownSyntax(content)) {
		return showText(content, isNoBreakline);
	}

	return renderMarkdown(content);
};

export const permitMessageContentType = (contentType: string) => {
	switch (contentType) {
		default:
			return false;
		case MESSAGE_TYPE.IMAGE_SET:
		case MESSAGE_TYPE.AUDIO_TYPE:
		case MESSAGE_TYPE.VIDEO_TYPE:
		case MESSAGE_TYPE.FILE_TYPE:
		case MESSAGE_TYPE.IMAGE_TYPE:
		case MESSAGE_TYPE.TEMPLATE_TYPE:
		case MESSAGE_TYPE.TEXT_PLAIN_TYPE:
		case MESSAGE_TYPE.X_CUSTOMER_FORM:
		case MESSAGE_TYPE.BOT_TYPING:
		case MESSAGE_TYPE.AGENT_PROCESSING:
		case MESSAGE_TYPE.SHARE_TYPE:
			return true;
	}
};

export const getMessageSummary = (message: any, displayName: any) => {
	const { customerToken } = getState().socketio;

	const { contentType, sender } = message;
	const newSender = sender === customerToken ? i18n.t('chat.you') : displayName;
	switch (contentType) {
		default:
			return '';
		case MESSAGE_TYPE.FILE_TYPE:
			return newSender ? `${i18n.t('chat.sentFile', { displayName: newSender })}` : i18n.t('chat.sentFile');
		case MESSAGE_TYPE.IMAGE_SET:
		case MESSAGE_TYPE.IMAGE_TYPE:
			return newSender ? `${i18n.t('chat.sentPicture', { displayName: newSender })}` : i18n.t('chat.sentPicture');
		case MESSAGE_TYPE.AUDIO_TYPE:
			return newSender ? `${i18n.t('chat.sentAudio', { displayName: newSender })}` : i18n.t('chat.sentVideo');
		case MESSAGE_TYPE.VIDEO_TYPE:
			return newSender ? `${i18n.t('chat.sentVideo', { displayName: newSender })}` : i18n.t('chat.sentVideo');
		case MESSAGE_TYPE.TEMPLATE_TYPE:
			if (message.data.templateType === 'text') {
				return newSender ? `${newSender}:${message.data.content}` : message.data.content;
			}
			return newSender ? `${i18n.t('chat.sentTemplate', { displayName: newSender })}` : i18n.t('chat.sentTemplate');

		case MESSAGE_TYPE.TEXT_PLAIN_TYPE:
			return newSender ? `${newSender}:${message.data.content}` : message.data.content;
	}
};

// 使用第三方 API 獲取公網 IP
const fetchPublicIP = async (): Promise<string | null> => {
	try {
		// 使用 ipify API（免費且可靠）
		const response = await axios.get('https://api.ipify.org?format=json', { timeout: 3000 });
		return response.data.ip;
	} catch (error) {
		logger.warn('failed to fetch public IP', error);
		return null;
	}
};

// 使用 WebRTC 獲取本地 IP（可能是內網 IP）
const getLocalIP = (): Promise<string[]> => {
	return new Promise(resolve => {
		const ips: string[] = [];
		const RTCPeerConnection =
			(window as any).RTCPeerConnection || (window as any).webkitRTCPeerConnection || (window as any).mozRTCPeerConnection;

		if (!RTCPeerConnection) {
			resolve([]);
			return;
		}

		const pc = new RTCPeerConnection({ iceServers: [] });
		pc.createDataChannel('');

		pc.createOffer().then((offer: any) => pc.setLocalDescription(offer));

		pc.onicecandidate = (ice: any) => {
			if (!ice || !ice.candidate || !ice.candidate.candidate) {
				resolve(ips);
				pc.close();
				return;
			}

			const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
			const ipMatch = ipRegex.exec(ice.candidate.candidate);
			if (ipMatch && !ips.includes(ipMatch[1])) {
				ips.push(ipMatch[1]);
			}
		};

		// 3秒後超時
		setTimeout(() => {
			resolve(ips);
			pc.close();
		}, 3000);
	});
};

// 獲取完整的 IP 資訊（同時嘗試公網和本地 IP）
const getIPInfo = async () => {
	const [publicIP, localIPs] = await Promise.all([fetchPublicIP(), getLocalIP()]);

	return {
		publicIP: publicIP || 'unavailable',
		localIPs: localIPs.length > 0 ? localIPs : ['unavailable'],
	};
};

const getClientMetadata = async () => {
	const { userAgent } = navigator;

	// 檢測裝置類型
	const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
	const isTablet = /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent);

	// 獲取 IP 資訊
	const ipInfo = await getIPInfo();

	return {
		// IP 資訊
		publicIP: ipInfo.publicIP,
		localIPs: ipInfo.localIPs,
		// 裝置資訊
		deviceType: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',

		userAgent,
	};
};

export const getMessageObject = async (sendData: any, type = 'text/plain') => {
	const { customerToken } = getState().socketio;

	const metadata = await getClientMetadata();

	return {
		sender: customerToken,
		senderType: 'Customer',
		data: sendData,
		contentType: type,
		receiptId: uuidv4(),
		createdAt: new Date().toISOString(),
		isPending: true,
		metadata,
	};
};

export const signPayload = (payload: any) => {
	const { customerToken } = getState().socketio;
	const nonce = new Date().getTime().toString();

	const baseString = CryptoJS.HmacSHA256(payload, `Super8_Customer/${customerToken}/${nonce}`).toString(CryptoJS.enc.Hex);

	const signature = CryptoJS.MD5(baseString).toString(CryptoJS.enc.Hex);

	const signedPayload = JSON.stringify({
		signature,
		nonce,
		data: payload,
	});
	return signedPayload;
};
